/* oxlint-disable tailwindcss/no-unknown-classes -- Styles are colocated in entiredb-cell-diagram.css. */
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react"

import { useElementWidth } from "@/hooks/useElementWidth"
import { useIsVisible } from "@/hooks/useIsVisible"
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"
import { cardRingClassName } from "@/routes/-components/home-new/homeNewStyles"

import { DiagramTooltip } from "./diagram-tooltip"
import { entireDbCellSteps } from "./forge-diagram-steps"

import "./entiredb-cell-diagram.css"

/*
 * One EntireDB cell: three availability-zone nodes around PostgreSQL. A push
 * fans out, stages, commits and moves main with a compare-and-swap; a fetch on
 * another node then reads main and fills the pack from a peer. One step per
 * entry in `entireDbCellSteps`, each one logical second long, played at 1/3.5
 * speed.
 */

// `getSceneValues` scripts its reveals for exactly this many one-second steps.
// Adding, removing or reordering a step in `entireDbCellSteps` means retiming
// the scene too, so fail loudly instead of letting the counter and timeline drift.
const scriptedStepCount = 10
if (entireDbCellSteps.length !== scriptedStepCount) {
  throw new Error(
    `entireDbCellSteps has ${entireDbCellSteps.length} steps, but the EntireDB cell scene is timed for ${scriptedStepCount}.`,
  )
}
const phaseCount = entireDbCellSteps.length
const duration = phaseCount + 0.6
const playbackRate = 1 / 3.5
const candidateSha = "3f2a"
const currentSha = "a91e"
// Below this container width the scene switches to its narrow frame.
const narrowWidth = 640

interface Step {
  end: number
  start: number
  text: string
  title: string
}

const steps: Step[] = entireDbCellSteps
  .map(({ title, caption }, index, all) => ({
    title,
    text: caption,
    end: index === all.length - 1 ? duration : index + 1,
  }))
  .map((step, index, all) => ({ ...step, start: index === 0 ? 0 : all[index - 1]!.end }))

interface Tip {
  body: string
  title: string
}

// Hover explanations for the scene's parts (see DiagramTooltip).
const tips = {
  push: {
    title: "Push client",
    body: "The remote helper picks one of the repository's providers and pushes the pack straight to it.",
  },
  fetch: {
    title: "Fetch client",
    body: "A separate client. One provider serves its fetch: it reads main from PostgreSQL and sends the pack from its own disk.",
  },
  cell: {
    title: "EntireDB cell",
    body: "One PostgreSQL database for refs and a set of EntireDB nodes for objects and packfiles. Each repository lives in one cell.",
  },
  nodes: [
    {
      title: "Node A · AZ-A",
      body: "This push's coordinator. It streams the pack to the other providers while staging its own copy, then asks them to promote it.",
    },
    {
      title: "Node B · AZ-B",
      body: "A provider in a second availability zone. It stages and promotes the pack, and can fill objects another provider is missing.",
    },
    {
      title: "Node C · AZ-C",
      body: "A provider in a third availability zone. Here it also serves the fetch, filling any missing objects from Node B first.",
    },
  ],
  staging: {
    title: "Staging · hidden",
    body: "Objects land here first, invisible to readers. They are promoted only once providers in at least two availability zones have staged the pack and its object graph checks out.",
  },
  store: {
    title: "Immutable objects",
    body: "The durable, content-addressed object store. Promoted packs land here, and objects never change once written.",
  },
  postgres: {
    title: "PostgreSQL · mutable refs",
    body: "Every ref lives in its own row. A push becomes visible only when the compare-and-swap on its ref succeeds.",
  },
} satisfies Record<string, Tip | Tip[]>

/* ---------- motion helpers ---------- */

function clamp01(value: number) {
  return Math.min(1, Math.max(0, value))
}

function easeOut(value: number) {
  return 1 - Math.pow(1 - clamp01(value), 3)
}

function riseAt(time: number, start: number, fade = 0.3) {
  return easeOut((time - start) / fade)
}

function pulse(time: number, start: number, end: number, fade = 0.3) {
  if (time <= start || time >= end) return 0
  return easeOut(Math.min((time - start) / fade, (end - time) / fade, 1))
}

function lerp(from: number, to: number, t: number) {
  return from + (to - from) * clamp01(t)
}

/** Types `text` out one character per `charSeconds`, with a block cursor. */
function typeOut(time: number, start: number, text: string, charSeconds = 0.04) {
  if (time <= start) return ""
  const shown = Math.floor((time - start) / charSeconds)
  if (shown >= text.length) return text
  return `${text.slice(0, Math.max(0, shown))}█`
}

const flipStagger = 0.013
const flipSpin = 0.094
const flipTick = 0.016
const flipGlyphs = "abcdefghijklmnopqrstuvwxyz0123456789"

/** Split-flap: each character spins through glyphs, staggered left to right. */
function splitFlap(text: string, previous: string, elapsed: number) {
  let prev = previous
  if (prev.length < text.length) prev = " ".repeat(text.length - prev.length) + prev
  else if (prev.length > text.length) prev = prev.slice(prev.length - text.length)
  let out = ""
  for (let i = 0; i < text.length; i++) {
    const start = i * flipStagger
    if (elapsed < start) {
      out += prev.charAt(i) || " "
    } else if (elapsed < start + flipSpin) {
      const glyph = Math.floor((elapsed - start + i * 0.002) / flipTick) % flipGlyphs.length
      out += flipGlyphs.charAt(glyph)
    } else {
      out += text.charAt(i)
    }
  }
  return out
}

function flowOffset(time: number, delay: number, period: number) {
  const t = (time + delay) / period
  return -100 * (t - Math.floor(t))
}

function stepIndexAt(time: number) {
  const index = steps.findIndex((step) => time < step.end)
  return index === -1 ? steps.length - 1 : index
}

/* ---------- geometry ---------- */

type Point = readonly [number, number]

/** An orthogonal polyline with rounded corners. */
function roundedPath(points: Point[], radius = 16) {
  const d = [`M${points[0]![0]} ${points[0]![1]}`]
  for (let i = 1; i < points.length - 1; i++) {
    const [x0, y0] = points[i - 1]!
    const [x1, y1] = points[i]!
    const [x2, y2] = points[i + 1]!
    const seg1 = Math.abs(x1 - x0) + Math.abs(y1 - y0)
    const seg2 = Math.abs(x2 - x1) + Math.abs(y2 - y1)
    const r = Math.min(radius, seg1 / 2, seg2 / 2)
    const d1x = Math.sign(x1 - x0)
    const d1y = Math.sign(y1 - y0)
    const d2x = Math.sign(x2 - x1)
    const d2y = Math.sign(y2 - y1)
    const sweep = d1x * d2y - d1y * d2x > 0 ? 1 : 0
    d.push(
      `L${x1 - d1x * r} ${y1 - d1y * r}`,
      `A${r} ${r} 0 0 ${sweep} ${x1 + d2x * r} ${y1 + d2y * r}`,
    )
  }
  const last = points[points.length - 1]!
  d.push(`L${last[0]} ${last[1]}`)
  return d.join(" ")
}

const tagChipW = 73
const chipH = 41
const cellHeaderBandH = 129
const nodeHeaderBandH = 129
const boxRx = 16
const innerRowInset = 16
const innerRowOffsetY = 49.5
const innerRowH = 63
const innerRowRx = 16
const packLabelInset = 18
const pgH = 98
const refBoxW = 183

// Half-widths of the labels that sit in a gap cut into a wire.
const packLabelHalfWidth = 38
const ackLabelHalfWidth = 32
const fetchLabelHalfWidth = 44
const packTipLabelHalfWidth = 74
const objectsLabelHalfWidth = 56

// Measured off the design frames: 1920 wide for the article column, 905 wide
// for phones (an independently drawn frame, not a scaled-down copy).
function buildGeometry(narrow: boolean) {
  const sceneWidth = narrow ? 905 : 1920
  const pushTag = narrow ? { x: 202, y: 28, w: 178, h: 64 } : { x: 735, y: 95, w: 178, h: 64 }
  const fetchTag = narrow ? { x: 438, y: 28, w: 263, h: 64 } : { x: 1009, y: 95, w: 263, h: 64 }
  const tagChipY = narrow ? pushTag.y + 11 : 106
  const tagChipCenterY = tagChipY + 20.5
  const pushChipX = narrow ? 293 : 826
  const fetchChipX = narrow ? 616 : 1187

  const cellX = narrow ? 20 : 56.5
  const cellW = narrow ? 865 : 1808
  const cellHeaderY = narrow ? pushTag.y + pushTag.h + 76 : 259.5
  const cellBodyY = narrow ? cellHeaderY + 80 : 340
  const cellIcon = { x: cellX + 23.5, y: cellHeaderY + 26.5, size: 28 }
  const cellLabelCenterY = cellHeaderY + (cellBodyY - cellHeaderY) / 2

  const nodeX = narrow ? [50, 327, 603] : [100.5, 691.5, 1281.5]
  const nodeCardW = narrow ? 252 : 539
  const nodeHeaderY = narrow ? cellHeaderY + 110 : 387.5

  const statusPill = { x: cellX + 442.5, y: cellHeaderY + 13.5, h: 52, r: 26 }
  const statusBaseline = statusPill.y + 32

  const nodeBodyY = narrow ? nodeHeaderY + 79.5 : 467.5
  const nodeBodyH = narrow ? 459 : 368
  const nodeBodyBottom = nodeBodyY + nodeBodyH
  const nameCenterY = nodeHeaderY + (nodeBodyY - nodeHeaderY) / 2
  const connectorY = nodeBodyY + 24.5

  const boxOffsetX = 24.5
  const boxW = nodeCardW - boxOffsetX * 2
  const boxH = narrow ? 176 : 129
  const captionOffsetY = narrow ? 38 : 30
  const innerRowOffsetX = boxOffsetX + innerRowInset
  const innerRowW = boxW - innerRowInset * 2
  const chipOffsetX = 127.5
  const statusInsetX = innerRowOffsetX + innerRowW - 26

  const stagingOuterY = connectorY
  const stagingRowY = stagingOuterY + innerRowOffsetY
  const stagingOuterBottom = stagingOuterY + boxH
  const storeOuterY = stagingOuterBottom + 21.5
  const storeRowY = storeOuterY + innerRowOffsetY

  const pgX = nodeX[0]!
  const pgY = narrow ? nodeBodyBottom + (nodeHeaderY - cellBodyY) * 2 : 884.5
  const pgW = narrow ? 805 : 1720
  const cellBodyBottom = narrow ? pgY + pgH + (pgX - cellX) : pgY + pgH + 46.5

  const nodeCx = nodeX.map((x) => x + nodeCardW / 2) as [number, number, number]
  const [node1Cx, node2Cx, node3Cx] = nodeCx
  const meshTrunkY = narrow ? statusPill.y + 26 : nodeHeaderY - 87.5

  const pushTagCx = pushTag.x + pushTag.w / 2
  const fetchTagCx = fetchTag.x + fetchTag.w / 2
  const pushTagBottom = pushTag.y + pushTag.h
  const fetchTagBottom = fetchTag.y + fetchTag.h
  const tagElbowY = narrow ? pushTagBottom + (cellHeaderY - pushTagBottom) / 2 : pushTagBottom + 45
  const wireLabelPushX = narrow ? 222 : 550
  const wireLabelFetchX = narrow ? 648 : 1342
  const objectsLabelX = (node2Cx + node3Cx) / 2

  const meshDown = (fromCx: number, toCx: number) =>
    roundedPath([
      [fromCx, meshTrunkY],
      [toCx, meshTrunkY],
      [toCx, nodeHeaderY],
    ])
  const meshUp = (fromCx: number, toCx: number) =>
    roundedPath([
      [fromCx, nodeHeaderY],
      [fromCx, meshTrunkY],
      [toCx, meshTrunkY],
      [toCx, nodeHeaderY],
    ])

  return {
    narrow,
    sceneWidth,
    sceneHeight: narrow ? cellBodyBottom + 28 : 1080,
    pushTag,
    fetchTag,
    tagChipCenterY,
    pushChipX,
    fetchChipX,
    cellX,
    cellW,
    cellHeaderY,
    cellBodyY,
    cellBodyBottom,
    cellIcon,
    cellLabelCenterY,
    nodeX,
    nodeCardW,
    nodeHeaderY,
    nodeBodyY,
    nodeBodyH,
    nodeBodyBottom,
    nameCenterY,
    statusPill,
    statusBaseline,
    statusWordMidX: (node2Cx + node3Cx) / 2,
    boxOffsetX,
    boxW,
    boxH,
    innerRowOffsetX,
    innerRowW,
    chipOffsetX,
    statusInsetX,
    stagingOuterY,
    stagingCaptionY: stagingOuterY + captionOffsetY,
    stagingRowY,
    stagingRowTextY: stagingRowY + innerRowH / 2,
    stagingOuterBottom,
    storeOuterY,
    storeCaptionY: storeOuterY + captionOffsetY,
    storeRowY,
    storeRowTextY: storeRowY + innerRowH / 2,
    footerBaseline: nodeBodyBottom - 32,
    pgX,
    pgY,
    pgW,
    refBoxOffsetX: narrow ? 590 : 540,
    refBoxY: pgY + 18,
    refChipOffsetX: narrow ? 687.5 : 637.5,
    mainLabelInset: narrow ? 32 : 22,
    pgAnnotationX: pgX + pgW - 32,
    node1Cx,
    node2Cx,
    node3Cx,
    meshTrunkY,
    tagElbowY,
    wireLabelPushX,
    wireLabelFetchX,
    objectsLabelX,
    meshTrunk: roundedPath([
      [node1Cx, nodeHeaderY],
      [node1Cx, meshTrunkY],
      [node3Cx, meshTrunkY],
      [node3Cx, nodeHeaderY],
    ]),
    meshDownB: meshDown(node1Cx, node2Cx),
    meshDownC: meshDown(node1Cx, node3Cx),
    meshUpB: meshUp(node2Cx, node1Cx),
    meshUpC: meshUp(node3Cx, node1Cx),
    pushWireDown: roundedPath([
      [pushTagCx, pushTagBottom],
      [pushTagCx, tagElbowY],
      [node1Cx, tagElbowY],
      [node1Cx, nodeHeaderY],
    ]),
    pushWireDownSegments: [
      roundedPath([
        [pushTagCx, pushTagBottom],
        [pushTagCx, tagElbowY],
        [wireLabelPushX + packLabelHalfWidth, tagElbowY],
      ]),
      roundedPath([
        [wireLabelPushX - packLabelHalfWidth, tagElbowY],
        [node1Cx, tagElbowY],
        [node1Cx, nodeHeaderY],
      ]),
    ] as const,
    pushWireUp: roundedPath([
      [node1Cx, nodeHeaderY],
      [node1Cx, tagElbowY],
      [pushTagCx, tagElbowY],
      [pushTagCx, pushTagBottom],
    ]),
    pushWireUpSegments: [
      roundedPath([
        [node1Cx, nodeHeaderY],
        [node1Cx, tagElbowY],
        [wireLabelPushX - ackLabelHalfWidth, tagElbowY],
      ]),
      roundedPath([
        [wireLabelPushX + ackLabelHalfWidth, tagElbowY],
        [pushTagCx, tagElbowY],
        [pushTagCx, pushTagBottom],
      ]),
    ] as const,
    fetchWireDown: roundedPath([
      [fetchTagCx, fetchTagBottom],
      [fetchTagCx, tagElbowY],
      [node3Cx, tagElbowY],
      [node3Cx, nodeHeaderY],
    ]),
    fetchWireDownSegments: [
      roundedPath([
        [fetchTagCx, fetchTagBottom],
        [fetchTagCx, tagElbowY],
        [wireLabelFetchX - fetchLabelHalfWidth, tagElbowY],
      ]),
      roundedPath([
        [wireLabelFetchX + fetchLabelHalfWidth, tagElbowY],
        [node3Cx, tagElbowY],
        [node3Cx, nodeHeaderY],
      ]),
    ] as const,
    fetchWireUp: roundedPath([
      [node3Cx, nodeHeaderY],
      [node3Cx, tagElbowY],
      [fetchTagCx, tagElbowY],
      [fetchTagCx, fetchTagBottom],
    ]),
    fetchWireUpSegments: [
      roundedPath([
        [node3Cx, nodeHeaderY],
        [node3Cx, tagElbowY],
        [wireLabelFetchX + packTipLabelHalfWidth, tagElbowY],
      ]),
      roundedPath([
        [wireLabelFetchX - packTipLabelHalfWidth, tagElbowY],
        [fetchTagCx, tagElbowY],
        [fetchTagCx, fetchTagBottom],
      ]),
    ] as const,
    fillFromNodeB: meshUp(node2Cx, node3Cx),
    fillFromNodeBSegments: [
      roundedPath([
        [node2Cx, nodeHeaderY],
        [node2Cx, meshTrunkY],
        [objectsLabelX - objectsLabelHalfWidth, meshTrunkY],
      ]),
      roundedPath([
        [objectsLabelX + objectsLabelHalfWidth, meshTrunkY],
        [node3Cx, meshTrunkY],
        [node3Cx, nodeHeaderY],
      ]),
    ] as const,
  }
}

type Geometry = ReturnType<typeof buildGeometry>

const wideGeometry = buildGeometry(false)
const narrowGeometry = buildGeometry(true)

/* ---------- scene timeline ---------- */

function getSceneValues(time: number) {
  const stageMoveFraction = riseAt(time, 3, 0.5)
  let stagePackOpacity = 0
  if (time >= 1 && time < 3) stagePackOpacity = riseAt(time, 1)
  else if (time >= 3) stagePackOpacity = lerp(1, 0, easeOut((time - 3) / 0.5))

  return {
    ackArrowOpacity: pulse(time, 6, 7),
    casArrowOpacity: pulse(time, 5, 6),
    casOpacity: pulse(time, 5, 7),
    commitAckOpacity: pulse(time, 4, 5),
    commitPackLabelOpacity: pulse(time, 3, 4),
    committedOpacity: riseAt(time, 4),
    durableCheckOpacity: riseAt(time, 4),
    durableFlip: riseAt(time, 3.6, 0.35),
    durableFlipElapsed: time - 3.6,
    durableResultsLabelOpacity: pulse(time, 4, 6),
    fanOutLabelOpacity: pulse(time, 0, 2),
    fetchLocalOrPeerOpacity: pulse(time, 8, 9),
    fetchOpenOpacity: pulse(time, 7, 8),
    fetchRefOpacity: riseAt(time, 7),
    finalFetchOpacity: riseAt(time, 9),
    finalFetchElapsed: time - 9,
    progress: steps.map((step) => clamp01((time - step.start) / (step.end - step.start))),
    promoteOpacity: pulse(time, 3, 4),
    refFlip: riseAt(time, 6),
    refFlipElapsed: time - 6,
    stageAckOpacity: pulse(time, 2, 3),
    stageCheckOpacity: riseAt(time, 2),
    stageMoveFraction,
    stagePackOpacity,
    stageResultsLabelOpacity: pulse(time, 2, 3),
    streamOpacity: pulse(time, 0, 1),
    validatedOpacity: pulse(time, 2, 4),
    validatingOpacity: pulse(time, 1, 2),
  }
}

type SceneValues = ReturnType<typeof getSceneValues>

/* ---------- hooks ---------- */

/* ---------- SVG parts ---------- */

function EntireMark({ x, y, size }: { x: number; y: number; size: number }) {
  return (
    <g className="edb-icon" transform={`translate(${x} ${y}) scale(${size / 32})`}>
      <path
        fill="currentColor"
        d="M17.6.22c1.61-.43,3.33-.21,4.77.61l7.47,4.25c.31.18.25.64-.09.73l-16.75,4.49c-1.69.45-2.69,2.19-2.24,3.88l.82,3.06c.45,1.69,2.19,2.69,3.88,2.24l9.57-2.5,4.61,4.74c.89.89,1.39,2.1,1.39,3.36v2.54c0,1.65-1.27,3.03-2.92,3.16l-15.61,1.2c-1.85.14-3.67-.53-4.98-1.85l-5.11-5.14c-.88-.89-1.38-2.09-1.38-3.35v-2.55c0-1.65,1.5-3.08,3-3.08h2l-3.85-3.04c-.6-.43-1.04-1.05-1.23-1.76l-.81-3.03c-.45-1.69.55-3.43,2.24-3.88L17.6.22Z"
      />
      <path
        fill="currentColor"
        d="M20.02 13.94a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0ZM28.02 11.94a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0Z"
      />
    </g>
  )
}

function DatabaseGlyph({ x, y, size }: { x: number; y: number; size: number }) {
  return (
    <g
      className="edb-icon"
      transform={`translate(${x} ${y}) scale(${size / 24})`}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5V19A9 3 0 0 0 21 19V5" />
      <path d="M3 12A9 3 0 0 0 21 12" />
    </g>
  )
}

interface ShaChipProps {
  elapsed?: number
  previousValue?: string
  tone: "candidate" | "current"
  value: string
  x: number
  y: number
}

/** A short SHA in a tinted pill; with `previousValue` it split-flaps over. */
function ShaChip({ elapsed = 0, previousValue, tone, value, x, y }: ShaChipProps) {
  const display = previousValue === undefined ? value : splitFlap(value, previousValue, elapsed)
  return (
    <g className={`edb-sha edb-sha--${tone}`}>
      <rect x={x} y={y - chipH / 2} width={tagChipW} height={chipH} rx={chipH / 3.6} />
      <text x={x + tagChipW / 2} y={y} dominantBaseline="central" textAnchor="middle">
        {display}
      </text>
    </g>
  )
}

const flowDotPeriod = 0.6
const flowDotGap = 100 / 4 - 1

/** Bright dashes travelling along a wire to show direction. */
function FlowDot({
  d,
  delay = 0,
  opacity,
  time,
}: {
  d: string
  delay?: number
  opacity: number
  time: number
}) {
  if (opacity <= 0.02) return null
  return (
    <path
      className="edb-flow-dot"
      d={d}
      pathLength={100}
      strokeDasharray={`1 ${flowDotGap}`}
      strokeDashoffset={flowOffset(time, delay, flowDotPeriod)}
      opacity={opacity}
    />
  )
}

interface WireProps {
  d: string
  delay?: number
  markerEnd?: string
  opacity: number
  strong?: boolean
  segments?: readonly [string, string]
  start: number
  time: number
}

/** A dashed wire that fades in at `start`, optionally split around a label. */
function Wire({
  d,
  delay = 0,
  markerEnd,
  opacity,
  strong = false,
  segments,
  start,
  time,
}: WireProps) {
  const wipe = easeOut((time - start) / 0.3)
  const marker = wipe >= 1 && markerEnd ? `url(#${markerEnd})` : undefined
  const className = strong ? "edb-wire edb-wire--strong" : "edb-wire"
  return (
    <>
      {segments ? (
        <>
          <path className={className} d={segments[0]} opacity={wipe} />
          <path className={className} d={segments[1]} opacity={wipe} markerEnd={marker} />
        </>
      ) : (
        <path className={className} d={d} opacity={wipe} markerEnd={marker} />
      )}
      <FlowDot d={d} opacity={wipe >= 1 ? opacity : 0} time={time} delay={delay} />
    </>
  )
}

interface NodeSectionProps {
  caption: string
  captionY: number
  clipId?: string
  dashed?: boolean
  g: Geometry
  outerY: number
  rowOffsetY?: number
  rowOpacity?: number
  rowTextY: number
  rowY: number
  status: ReactNode
  tip: Tip
  value: ReactNode
  x: number
}

/** A captioned box inside a node card holding one "pack <sha> … status" row. */
function NodeSection({
  caption,
  captionY,
  clipId,
  dashed = false,
  g,
  outerY,
  rowOffsetY,
  rowOpacity = 1,
  rowTextY,
  rowY,
  status,
  tip,
  value,
  x,
}: NodeSectionProps) {
  const row = (
    <g
      clipPath={clipId ? `url(#${clipId})` : undefined}
      style={rowOffsetY ? { transform: `translateY(${rowOffsetY}px)` } : undefined}
    >
      <rect
        className="edb-row"
        x={x + g.innerRowOffsetX}
        y={rowY}
        width={g.innerRowW}
        height={innerRowH}
        rx={innerRowRx}
      />
      <g opacity={rowOpacity}>
        <text
          className="edb-small edb-mono"
          x={x + g.innerRowOffsetX + packLabelInset}
          y={rowTextY}
          dominantBaseline="central"
        >
          pack
        </text>
        {value}
      </g>
      {status}
    </g>
  )
  const box = (
    <>
      {/* The box itself is unfilled; this catches hovers anywhere inside it. */}
      <rect
        className="edb-hit"
        x={x + g.boxOffsetX}
        y={outerY}
        width={g.boxW}
        height={g.boxH}
        rx={boxRx}
      />
      <rect
        className={dashed ? "edb-stagebox" : "edb-store"}
        x={x + g.boxOffsetX}
        y={outerY}
        width={g.boxW}
        height={g.boxH}
        rx={boxRx}
      />
      {g.narrow ? (
        <text
          className="edb-small"
          x={x + g.boxOffsetX + g.boxW / 2}
          y={(outerY + rowY) / 2}
          textAnchor="middle"
          dominantBaseline="central"
        >
          {caption}
        </text>
      ) : (
        <text className="edb-small" x={x + g.boxOffsetX + 19.5} y={captionY}>
          {caption}
        </text>
      )}
    </>
  )

  // A sliding row paints behind its own box and is clipped to it, so it
  // vanishes as it crosses the box's bottom edge.
  if (clipId) {
    return (
      <g data-tip-title={tip.title} data-tip={tip.body}>
        <clipPath id={clipId}>
          <rect x={x + g.boxOffsetX} y={outerY} width={g.boxW} height={g.boxH} />
        </clipPath>
        {row}
        {box}
      </g>
    )
  }
  return (
    <g data-tip-title={tip.title} data-tip={tip.body}>
      {box}
      {row}
    </g>
  )
}

const nodeNames = [
  ["Node A", "AZ-A"],
  ["Node B", "AZ-B"],
  ["Node C", "AZ-C"],
] as const

function NodeCard({
  footer,
  g,
  idPrefix,
  index,
  time,
  values,
}: {
  footer?: ReactNode
  g: Geometry
  idPrefix: string
  index: number
  time: number
  values: SceneValues
}) {
  const x = g.nodeX[index]!
  const [name, az] = nodeNames[index]!
  const statusX = x + g.statusInsetX
  const tip = tips.nodes[index]!

  return (
    <g data-tip-title={tip.title} data-tip={tip.body}>
      <rect
        className="edb-node"
        x={x}
        y={g.nodeHeaderY}
        width={g.nodeCardW}
        height={nodeHeaderBandH}
        rx={24}
      />
      <rect
        className="edb-node-lower"
        x={x}
        y={g.nodeBodyY}
        width={g.nodeCardW}
        height={g.nodeBodyH}
        rx={24}
      />
      <text className="edb-name" x={x + 32} y={g.nameCenterY} dominantBaseline="central">
        {name}
      </text>
      <text
        className="edb-lbl"
        x={x + g.nodeCardW - 32}
        y={g.nameCenterY}
        textAnchor="end"
        dominantBaseline="central"
      >
        {az}
      </text>

      <NodeSection
        g={g}
        x={x}
        caption="STAGING · HIDDEN"
        captionY={g.stagingCaptionY}
        clipId={`${idPrefix}-stage-${index}`}
        dashed
        tip={tips.staging}
        outerY={g.stagingOuterY}
        rowOffsetY={values.stageMoveFraction * (g.storeRowY - g.stagingRowY)}
        rowOpacity={values.stagePackOpacity}
        rowTextY={g.stagingRowTextY}
        rowY={g.stagingRowY}
        value={
          <ShaChip
            tone="candidate"
            value={candidateSha}
            x={x + g.chipOffsetX}
            y={g.stagingRowTextY}
          />
        }
        status={
          g.narrow ? null : (
            <text
              className="edb-small edb-accent"
              x={statusX}
              y={g.stagingRowTextY}
              textAnchor="end"
              dominantBaseline="central"
              opacity={values.stageCheckOpacity}
            >
              {typeOut(time, 2, "staged")}
            </text>
          )
        }
      />
      <NodeSection
        g={g}
        x={x}
        caption="IMMUTABLE OBJECTS"
        captionY={g.storeCaptionY}
        tip={tips.store}
        outerY={g.storeOuterY}
        rowTextY={g.storeRowTextY}
        rowY={g.storeRowY}
        value={
          <ShaChip
            tone={values.durableFlip >= 0.5 ? "candidate" : "current"}
            value={candidateSha}
            previousValue={currentSha}
            elapsed={values.durableFlipElapsed}
            x={x + g.chipOffsetX}
            y={g.storeRowTextY}
          />
        }
        status={
          g.narrow ? null : (
            <text
              className="edb-small edb-accent"
              x={statusX}
              y={g.storeRowTextY}
              textAnchor="end"
              dominantBaseline="central"
              opacity={values.durableCheckOpacity}
            >
              {typeOut(time, 4, "durable")}
            </text>
          )
        }
      />
      {footer}
    </g>
  )
}

/** The rotating status word on the cell's header bar. */
function StatusWord({
  g,
  label,
  opacity,
  width,
}: {
  g: Geometry
  label: string
  opacity: number
  width: number
}) {
  const x = g.narrow ? g.statusWordMidX - width / 2 : g.statusPill.x
  return (
    <g opacity={opacity}>
      <rect
        className="edb-status-bg"
        x={x}
        y={g.statusPill.y}
        width={width}
        height={g.statusPill.h}
        rx={g.statusPill.r}
      />
      <text className="edb-small edb-mono edb-status-text" x={x + 16} y={g.statusBaseline}>
        {label}
      </text>
    </g>
  )
}

function FooterText({
  accent = false,
  children,
  g,
  index,
  opacity,
}: {
  accent?: boolean
  children: string
  g: Geometry
  index: number
  opacity: number
}) {
  return (
    <text
      className={accent ? "edb-small edb-accent" : "edb-small"}
      x={g.nodeX[index]! + g.nodeCardW - 32}
      y={g.footerBaseline}
      textAnchor="end"
      dominantBaseline="central"
      opacity={opacity}
    >
      {children}
    </text>
  )
}

function CellScene({
  g,
  idPrefix,
  time,
  values: v,
}: {
  g: Geometry
  idPrefix: string
  time: number
  values: SceneValues
}) {
  const arrowStrong = `${idPrefix}-ah-strong`
  const arrowLine = `${idPrefix}-ah-line`
  const arrowSmall = `${idPrefix}-ah-sm`
  const pgCenterY = g.pgY + pgH / 2
  const casPath = `M${g.node1Cx} ${g.nodeBodyBottom} V${g.pgY}`
  const readPath = `M${g.node3Cx - 50} ${g.nodeBodyBottom} V${g.pgY}`

  return (
    <svg
      viewBox={`0 0 ${g.sceneWidth} ${g.sceneHeight}`}
      className="edb-svg"
      role="img"
      aria-label="An EntireDB cell: Node A, Node B and Node C in three availability zones above PostgreSQL, which holds the mutable main ref."
    >
      <defs>
        {[
          [arrowStrong, "edb-arrow edb-arrow--strong", 7],
          [arrowLine, "edb-arrow", 7],
          [arrowSmall, "edb-arrow", 4],
        ].map(([id, className, size]) => (
          <marker
            key={id}
            id={String(id)}
            viewBox="0 0 10 10"
            refX="8.5"
            refY="5"
            markerWidth={size}
            markerHeight={size}
            orient="auto-start-reverse"
          >
            <path className={String(className)} d="M0 .8 9.5 5 0 9.2z" />
          </marker>
        ))}
      </defs>

      {/* push / fetch tags above the cell */}
      <g data-tip-title={tips.push.title} data-tip={tips.push.body}>
        <rect
          className="edb-tag"
          x={g.pushTag.x}
          y={g.pushTag.y}
          width={g.pushTag.w}
          height={g.pushTag.h}
          rx={16}
        />
        <text
          className="edb-mono edb-tag-text"
          x={g.pushTag.x + 22}
          y={g.tagChipCenterY}
          dominantBaseline="central"
        >
          push
        </text>
        <ShaChip tone="candidate" value={candidateSha} x={g.pushChipX} y={g.tagChipCenterY} />
      </g>
      <g data-tip-title={tips.fetch.title} data-tip={tips.fetch.body}>
        <rect
          className="edb-tag"
          x={g.fetchTag.x}
          y={g.fetchTag.y}
          width={g.fetchTag.w}
          height={g.fetchTag.h}
          rx={16}
        />
        <text
          className="edb-mono edb-tag-text"
          x={g.fetchTag.x + 22}
          y={g.tagChipCenterY}
          dominantBaseline="central"
        >
          fetch main
        </text>
        <ShaChip
          tone={v.finalFetchOpacity >= 0.5 ? "candidate" : "current"}
          value={candidateSha}
          previousValue={currentSha}
          elapsed={v.finalFetchElapsed}
          x={g.fetchChipX}
          y={g.tagChipCenterY}
        />
      </g>

      {/* the cell: header band over a body */}
      <rect
        className="edb-cell-header"
        x={g.cellX}
        y={g.cellHeaderY}
        width={g.cellW}
        height={cellHeaderBandH}
        rx={24}
      />
      <rect
        className="edb-cell-body"
        x={g.cellX}
        y={g.cellBodyY}
        width={g.cellW}
        height={g.cellBodyBottom - g.cellBodyY}
        rx={24}
      />
      {/* the persistent mesh between the three nodes */}
      <path className="edb-wire edb-wire--mesh" d={g.meshTrunk} />
      <path
        className="edb-wire edb-wire--mesh"
        d={`M${g.node2Cx} ${g.meshTrunkY} V${g.nodeHeaderY}`}
      />

      <NodeCard
        g={g}
        idPrefix={idPrefix}
        index={0}
        time={time}
        values={v}
        footer={
          <>
            <FooterText g={g} index={0} opacity={v.validatingOpacity}>
              {typeOut(time, 1, "validating graph…")}
            </FooterText>
            <FooterText g={g} index={0} opacity={v.validatedOpacity} accent>
              {typeOut(time, 2, "graph complete")}
            </FooterText>
            <FooterText g={g} index={0} opacity={v.committedOpacity} accent>
              {typeOut(time, 4, "two AZs durable")}
            </FooterText>
          </>
        }
      />
      <NodeCard g={g} idPrefix={idPrefix} index={1} time={time} values={v} />
      <NodeCard
        g={g}
        idPrefix={idPrefix}
        index={2}
        time={time}
        values={v}
        footer={
          <FooterText g={g} index={2} opacity={v.fetchLocalOrPeerOpacity} accent>
            {typeOut(time, 8, "filled from Node B")}
          </FooterText>
        }
      />

      {/* 01 fan out: push tag to Node A, Node A to B and C */}
      <g opacity={v.streamOpacity}>
        <Wire
          d={g.pushWireDown}
          segments={g.pushWireDownSegments}
          markerEnd={arrowStrong}
          opacity={v.streamOpacity}
          strong
          start={0}
          time={time}
        />
        <path className="edb-wire" d={g.meshDownB} markerEnd={`url(#${arrowLine})`} />
        <path className="edb-wire" d={g.meshDownC} markerEnd={`url(#${arrowLine})`} />
        <FlowDot d={g.meshDownB} opacity={v.streamOpacity} time={time} delay={0.35} />
        <FlowDot d={g.meshDownC} opacity={v.streamOpacity} time={time} delay={0.7} />
        <text
          className="edb-small edb-wire-label"
          x={g.wireLabelPushX}
          y={g.tagElbowY}
          textAnchor="middle"
          dominantBaseline="central"
        >
          pack
        </text>
      </g>

      {/* 03 stage results back to Node A */}
      <g opacity={v.stageAckOpacity}>
        <path className="edb-wire" d={g.meshUpB} />
        <path className="edb-wire" d={g.meshUpC} markerEnd={`url(#${arrowLine})`} />
        <FlowDot d={g.meshUpB} opacity={v.stageAckOpacity} time={time} />
        <FlowDot d={g.meshUpC} opacity={v.stageAckOpacity} time={time} delay={0.45} />
      </g>

      <StatusWord g={g} label="fan out" opacity={v.fanOutLabelOpacity} width={131} />
      <StatusWord g={g} label="stage results" opacity={v.stageResultsLabelOpacity} width={227} />
      <StatusWord g={g} label="commit pack" opacity={v.commitPackLabelOpacity} width={196} />
      <StatusWord
        g={g}
        label="durable results"
        opacity={v.durableResultsLabelOpacity}
        width={257}
      />

      {/* 04 commit: promote staged packs into immutable storage */}
      <g opacity={v.promoteOpacity}>
        <path className="edb-wire" d={g.meshDownB} markerEnd={`url(#${arrowLine})`} />
        <path className="edb-wire" d={g.meshDownC} markerEnd={`url(#${arrowLine})`} />
        <FlowDot d={g.meshDownB} opacity={v.promoteOpacity} time={time} />
        <FlowDot d={g.meshDownC} opacity={v.promoteOpacity} time={time} delay={0.45} />
        {[g.node1Cx, g.node2Cx, g.node3Cx].map((cx) => (
          <path
            key={cx}
            className="edb-wire"
            d={`M${cx} ${g.stagingOuterBottom} V${g.storeOuterY}`}
            markerEnd={`url(#${arrowSmall})`}
          />
        ))}
      </g>

      {/* 05 durable results back to Node A */}
      <g opacity={v.commitAckOpacity}>
        <path className="edb-wire" d={g.meshUpB} />
        <path className="edb-wire" d={g.meshUpC} markerEnd={`url(#${arrowLine})`} />
        <FlowDot d={g.meshUpB} opacity={v.commitAckOpacity} time={time} />
        <FlowDot d={g.meshUpC} opacity={v.commitAckOpacity} time={time} delay={0.45} />
      </g>

      {/* PostgreSQL: the mutable refs */}
      <g data-tip-title={tips.postgres.title} data-tip={tips.postgres.body}>
        <rect className="edb-node" x={g.pgX} y={g.pgY} width={g.pgW} height={pgH} rx={24} />
        <DatabaseGlyph x={g.pgX + 30} y={pgCenterY - 14} size={28} />
        <text className="edb-lbl edb-lbl--lg" x={g.pgX + 74} y={pgCenterY + 7}>
          POSTGRESQL · MUTABLE REFS
        </text>
        <rect
          className="edb-row"
          x={g.pgX + g.refBoxOffsetX}
          y={g.refBoxY}
          width={refBoxW}
          height={63}
          rx={16}
        />
        <text
          className="edb-small edb-mono"
          x={g.pgX + g.refBoxOffsetX + g.mainLabelInset}
          y={pgCenterY}
          dominantBaseline="central"
        >
          main
        </text>
        <ShaChip
          tone={v.refFlip >= 0.5 ? "candidate" : "current"}
          value={candidateSha}
          previousValue={currentSha}
          elapsed={v.refFlipElapsed}
          x={g.pgX + g.refChipOffsetX}
          y={pgCenterY}
        />
        {g.narrow ? null : (
          <>
            <text
              className="edb-note edb-accent"
              x={g.pgAnnotationX}
              y={pgCenterY + 7}
              textAnchor="end"
              opacity={v.casOpacity}
            >
              {typeOut(time, 5, "CAS succeeds")}
            </text>
            <g opacity={v.fetchRefOpacity}>
              <text
                className="edb-note edb-accent"
                x={g.pgAnnotationX - tagChipW - 14}
                y={pgCenterY + 7}
                textAnchor="end"
              >
                {typeOut(time, 7, "Node C reads main")}
              </text>
              <ShaChip
                tone="candidate"
                value={candidateSha}
                x={g.pgAnnotationX - tagChipW}
                y={pgCenterY}
              />
            </g>
          </>
        )}
      </g>

      {/* 06 CAS: Node A moves main */}
      <g opacity={v.casArrowOpacity}>
        <path
          className="edb-wire edb-wire--strong"
          d={casPath}
          markerEnd={`url(#${arrowStrong})`}
        />
        <FlowDot d={casPath} opacity={v.casArrowOpacity} time={time} />
      </g>

      {/* 07 ack back to the push */}
      <g opacity={v.ackArrowOpacity}>
        <Wire
          d={g.pushWireUp}
          segments={g.pushWireUpSegments}
          markerEnd={arrowStrong}
          opacity={v.ackArrowOpacity}
          strong
          start={6}
          time={time}
        />
        <text
          className="edb-small edb-wire-label"
          x={g.wireLabelPushX}
          y={g.tagElbowY}
          textAnchor="middle"
          dominantBaseline="central"
        >
          {typeOut(time, 6, "ack")}
        </text>
      </g>

      {/* 08 fetch opens on Node C, which reads main */}
      <g opacity={v.fetchOpenOpacity}>
        <Wire
          d={g.fetchWireDown}
          segments={g.fetchWireDownSegments}
          markerEnd={arrowStrong}
          opacity={v.fetchOpenOpacity}
          strong
          start={7}
          time={time}
        />
        <path
          className="edb-wire edb-wire--strong"
          d={readPath}
          markerEnd={`url(#${arrowStrong})`}
        />
        <Wire
          d={`M${g.node3Cx + 50} ${g.pgY} V${g.nodeBodyBottom}`}
          delay={0.9}
          markerEnd={arrowStrong}
          opacity={v.fetchOpenOpacity}
          strong
          start={7}
          time={time}
        />
        <FlowDot d={readPath} opacity={v.fetchOpenOpacity} time={time} delay={0.55} />
        <text
          className="edb-small"
          x={g.node3Cx - 95}
          y={g.nodeBodyBottom + 32}
          textAnchor="middle"
        >
          read
        </text>
        <text
          className="edb-small"
          x={g.node3Cx + 95}
          y={g.nodeBodyBottom + 32}
          textAnchor="middle"
        >
          ref
        </text>
        <text
          className="edb-small edb-wire-label"
          x={g.wireLabelFetchX}
          y={g.tagElbowY}
          textAnchor="middle"
          dominantBaseline="central"
        >
          fetch
        </text>
      </g>

      {/* 09 Node C fills objects from Node B */}
      <g opacity={v.fetchLocalOrPeerOpacity}>
        <Wire
          d={g.fillFromNodeB}
          segments={g.fillFromNodeBSegments}
          markerEnd={arrowLine}
          opacity={v.fetchLocalOrPeerOpacity}
          start={8}
          time={time}
        />
        <text
          className="edb-small"
          x={g.objectsLabelX}
          y={g.meshTrunkY}
          textAnchor="middle"
          dominantBaseline="central"
        >
          objects
        </text>
      </g>

      {/* 10 the fetch resolves pack + tip */}
      <g opacity={v.finalFetchOpacity}>
        <Wire
          d={g.fetchWireUp}
          segments={g.fetchWireUpSegments}
          markerEnd={arrowStrong}
          opacity={v.finalFetchOpacity}
          strong
          start={9}
          time={time}
        />
        <text
          className="edb-small edb-wire-label"
          x={g.wireLabelFetchX}
          y={g.tagElbowY}
          textAnchor="middle"
          dominantBaseline="central"
        >
          pack + tip
        </text>
      </g>

      {/* The cell's name, painted last on a header-coloured plate: in the
          narrow frame the mesh trunk and the push wire run behind it. The
          hit area spans the header band above the cell body. */}
      <g data-tip-title={tips.cell.title} data-tip={tips.cell.body}>
        <rect
          className="edb-hit"
          x={g.cellX}
          y={g.cellHeaderY}
          width={g.cellW}
          height={g.cellBodyY - g.cellHeaderY}
          rx={24}
        />
        <rect
          className="edb-status-bg"
          x={g.cellIcon.x - 10}
          y={g.cellLabelCenterY - 26}
          width={g.cellIcon.size + 22 + 236}
          height={52}
          rx={26}
        />
        <EntireMark x={g.cellIcon.x} y={g.cellIcon.y} size={g.cellIcon.size} />
        <text
          className="edb-lbl edb-lbl--lg"
          x={g.cellIcon.x + g.cellIcon.size + 22}
          y={g.cellLabelCenterY}
          dominantBaseline="central"
        >
          ENTIREDB CELL
        </text>
      </g>
    </svg>
  )
}

/* ---------- the figure ---------- */

// A step's settled state: far enough in that its labels have faded in, its
// wires have drawn, its status lines have typed out and its SHAs have flipped,
// but before the pulses that end with the step start fading out. Shown with
// reduced motion and when a step is picked while paused.
function settledTime(index: number) {
  return steps[index]!.start + 0.75
}

/**
 * Animated push then fetch inside one EntireDB cell. Plays only while on
 * screen; with reduced motion it shows the final step, and the step bar picks
 * which settled step to show.
 */
export function EntireDbCellDiagram() {
  const rootRef = useRef<HTMLElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const previousFrameRef = useRef<number | null>(null)
  // Set when a step is picked while paused: playback resumes from its start.
  const resumeFromRef = useRef<number | null>(null)
  const idPrefix = `edb${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`
  const [time, setTime] = useState(0)
  const [staticStep, setStaticStep] = useState(steps.length - 1)
  const [isPlaying, setIsPlaying] = useState(true)
  const width = useElementWidth(viewportRef)
  const isVisible = useIsVisible(rootRef)
  const prefersReducedMotion = usePrefersReducedMotion()

  const displayTime = prefersReducedMotion ? settledTime(staticStep) : time
  const geometry = width < narrowWidth ? narrowGeometry : wideGeometry
  const values = useMemo(() => getSceneValues(displayTime), [displayTime])
  const stepIndex = stepIndexAt(displayTime)
  const step = steps[stepIndex]!

  useEffect(() => {
    if (!isVisible || !isPlaying || prefersReducedMotion) {
      previousFrameRef.current = null
      return
    }

    let frame = 0
    const animate = (timestamp: number) => {
      const previous = previousFrameRef.current ?? timestamp
      const delta = Math.min(0.06, (timestamp - previous) / 1000)
      previousFrameRef.current = timestamp
      setTime((current) => {
        const next = current + delta * playbackRate
        return next >= duration ? 0 : next
      })
      frame = window.requestAnimationFrame(animate)
    }

    frame = window.requestAnimationFrame(animate)
    return () => {
      window.cancelAnimationFrame(frame)
      previousFrameRef.current = null
    }
  }, [isPlaying, isVisible, prefersReducedMotion])

  function jumpTo(index: number) {
    if (prefersReducedMotion) {
      setStaticStep(index)
    } else if (isPlaying) {
      resumeFromRef.current = null
      setTime(steps[index]!.start)
    } else {
      // Paused: show the step settled, so the frame matches its caption.
      resumeFromRef.current = steps[index]!.start
      setTime(settledTime(index))
    }
  }

  function togglePlaying() {
    if (!isPlaying && resumeFromRef.current !== null) {
      setTime(resumeFromRef.current)
      resumeFromRef.current = null
    }
    setIsPlaying((playing) => !playing)
  }

  return (
    <figure
      ref={rootRef}
      aria-label="A push, then a fetch, inside one EntireDB cell"
      data-layout={geometry.narrow ? "narrow" : "wide"}
      className={`edb-cell not-prose my-10 overflow-hidden rounded-xl bg-raised font-sans ${cardRingClassName}`}
    >
      <div className="flex h-10 items-center gap-2 border-b border-default px-4 text-xs leading-4">
        <span className="shrink-0 text-muted">EntireDB cell</span>
        <span className="min-w-0 flex-1 truncate text-right font-semibold text-default">
          First the objects, then the branch
        </span>
      </div>

      <div ref={viewportRef} className="edb-viewport">
        <CellScene g={geometry} idPrefix={idPrefix} time={displayTime} values={values} />
      </div>

      <div className="edb-segments">
        {steps.map((item, index) => (
          <button
            key={item.title}
            type="button"
            aria-label={`Step ${index + 1}: ${item.title}`}
            aria-current={index === stepIndex ? "step" : undefined}
            onClick={() => jumpTo(index)}
          >
            <span className="edb-segment-track">
              <span
                className="edb-segment-fill"
                style={{ width: `${(values.progress[index] ?? 0) * 100}%` }}
              />
            </span>
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-1.5 px-4 pt-3 pb-4 sm:px-5">
        <div className="flex h-6 items-center gap-2 text-xs leading-4">
          <span className="shrink-0 font-mono text-muted tabular-nums">
            {String(stepIndex + 1).padStart(2, "0")} / {phaseCount}
          </span>
          <span className="min-w-0 flex-1 truncate font-semibold text-default">{step.title}</span>
          {prefersReducedMotion ? null : (
            <button
              type="button"
              className="edb-play"
              aria-label={isPlaying ? "Pause the animation" : "Play the animation"}
              onClick={togglePlaying}
            >
              <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                {isPlaying ? (
                  <>
                    <rect x="3" y="2" width="4" height="12" rx="1" />
                    <rect x="9" y="2" width="4" height="12" rx="1" />
                  </>
                ) : (
                  <path d="M4 2.5v11l10-5.5z" />
                )}
              </svg>
            </button>
          )}
        </div>
        <figcaption className="text-sm leading-5 text-muted">{step.text}</figcaption>
      </div>
      <DiagramTooltip rootRef={rootRef} />
    </figure>
  )
}
